import React from "react";
import FormLayout from '../FormLayout';
import TextField from './TextField';
import Utils from '../../services/Utils';
import MultiSelectField from '../../components/MultiSelectField/MultiSelectField';
import SelectField from '../../components/SelectField/SelectField';

/** Language (ISO 639-1); option lists match the movie event “original language” field. */
class LanguageField extends TextField {

    constructor(properties) {
        super({
            ...(LanguageField.getDefaultOptions()),
            ...properties,
        });

        this.type = FormLayout.LANGUAGE;
    }

    static getDefaultOptions() {
        const properties = super.getDefaultOptions();
        const existing = properties.content && typeof properties.content === 'object' ? properties.content : {};
        const defaults = Array.isArray(existing.defaults) ? existing.defaults : [];
        // Persist in content (Kore Field has no showNoDialog column); hydrate UI flag from either shape.
        const showNoDialog = !!(properties.showNoDialog ?? existing.show_no_dialog);
        properties.content = { ...existing, defaults, show_no_dialog: showNoDialog };
        properties.multiple = !!properties.multiple;
        properties.showNoDialog = showNoDialog;

        return properties;
    }

    getHandledOptions() {
        return super.getHandledOptions()
            .filter(o => o !== FormLayout.OPTION_PLACEHOLDER)
            .concat([
                FormLayout.OPTION_MULTIPLE,
                FormLayout.OPTION_DEFAULTS,
                FormLayout.OPTION_NO_DIALOG,
            ]);
    }

    language_select_options(ui_lang) {
        const languages = Utils.sorted_standard_languages(ui_lang || 'fr', {
            includeNoDialog: this.showNoDialog,
        });

        return languages.map(l => ({
            key: l.code,
            value: l.code,
            text: l.language,
        }));
    }

    getFormInitialValue() {
        const raw = this.content?.defaults;
        if (!raw || !Array.isArray(raw) || raw.length === 0) {
            return undefined;
        }
        const codes = raw.map(String).filter(Boolean);
        if (!codes.length) {
            return undefined;
        }
        if (this.multiple) {
            return codes;
        }
        return codes[0];
    }

    getInput(context) {
        const lang = context?.lang || 'fr';
        const options = this.language_select_options(lang);

        if (this.multiple) {
            return (
                <MultiSelectField
                    name={this.reduxFieldName(context)}
                    options={options}
                    placeholder="---"
                />
            );
        }

        return (
            <SelectField
                name={this.reduxFieldName(context)}
                options={options}
                placeholder="---"
            />
        );
    }

    renderValue(value, context) {
        if (value == null || value === '') {
            return super.renderValue(value, context);
        }
        const ui_lang = context?.lang || 'fr';
        if (Array.isArray(value)) {
            return value
                .map(v => Utils.standard_language_display_name(String(v), ui_lang))
                .join(', ');
        }
        return Utils.standard_language_display_name(String(value), ui_lang);
    }

    getIcon() {
        return 'language';
    }
}

export default LanguageField;
